import {
  keepPreviousData,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { HttpError } from "@nestia/fetcher";

import { getToken, revokeToken } from "../http";
import {
  isEmailNotVerifiedError,
  WAITING_ROOM_PATH,
} from "@shared/lib/email-verification";

/**
 * Fallback for a stale client: if any query/mutation is rejected by the
 * backend's email-verification gate, send the user to the waiting room.
 */
const redirectToWaitingRoomOnVerificationError = (error: unknown): void => {
  if (
    isEmailNotVerifiedError(error) &&
    window.location.pathname !== WAITING_ROOM_PATH
  ) {
    window.location.assign(WAITING_ROOM_PATH);
  }
};

/**
 * The server no longer accepts the session (e.g. the account was deactivated).
 * Not `logOut`: the order draft and the remembered company must survive.
 */
const redirectToLoginOnUnauthorized = (error: unknown): void => {
  if (
    error instanceof HttpError &&
    error.status === 401 &&
    getToken() &&
    window.location.pathname !== "/login"
  ) {
    revokeToken();
    window.location.assign("/login");
  }
};

export const onRequestError = (error: unknown): void => {
  redirectToWaitingRoomOnVerificationError(error);
  redirectToLoginOnUnauthorized(error);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onRequestError }),
  mutationCache: new MutationCache({ onError: onRequestError }),
  defaultOptions: {
    queries: {
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});
